import { Button, Flex, Text } from '#atoms';
import { Modal } from '#molecules';
import dayjs from 'dayjs';
import { useState } from 'react';
import styled from 'styled-components';

type ItemProps = {
  title: string;
  startDate: Date;
  endDate?: Date;
  onEdit?: () => void;
  onDelete?: () => void;
};

export default function HistoryItem({
  title,
  startDate,
  endDate,
  onDelete,
  onEdit
}: ItemProps) {
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const formattedStartDate = dayjs(startDate).format('YYYY.MM');
  const formattedEndDate = dayjs(endDate).format('YYYY.MM');

  const close = () => {
    setIsOpenDeleteModal(false);
  };

  return (
    <>
      <Wrapper align="center" justify="space-between">
        <Flex align="center" gap={16}>
          <IconWrapper onClick={() => setIsOpenDeleteModal(true)}>
            <TrashIcon />
          </IconWrapper>
          <Flex direction="column" gap={4}>
            <Title>{title}</Title>
            <FormattedDate>
              {formattedStartDate} {endDate ? `- ${formattedEndDate}` : ''}
            </FormattedDate>
          </Flex>
        </Flex>
        <div onClick={onEdit}>
          <RightIcon />
        </div>
      </Wrapper>
      {isOpenDeleteModal && (
        <Modal onClose={close}>
          <div>
            <Text style={{ textAlign: 'center' }}>삭제하시겠습니까?</Text>
          </div>
          <Flex fullWidth gap={12}>
            <Button background="anti" onClick={close}>
              취소
            </Button>
            <Button
              background="danger"
              onClick={async () => {
                if (onDelete) await onDelete();
                close();
              }}
            >
              삭제
            </Button>
          </Flex>
        </Modal>
      )}
    </>
  );
}

const Wrapper = styled(Flex)`
  padding-top: 8px;
  padding-bottom: 8px;
  padding-right: 4px;
`;

const Title = styled.span`
  color: ${({ theme }) => theme.palette.gray15};
  font-weight: 500;
`;

const FormattedDate = styled.span`
  color: ${({ theme }) => theme.palette.gray100};
  font-size: 12px;
`;

const IconWrapper = styled.div`
  padding: 3px;
  cursor: pointer;
`;

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="17"
    viewBox="0 0 14 17"
    fill="none"
  >
    <g clipPath="url(#clip0_24_2412)">
      <path
        d="M8.76051 1.2115L9.65576 1.20975L9.65376 0.44675C9.65376 0.32875 9.60576 0.21375 9.52226 0.1305C9.43926 0.0472501 9.32326 -0.000499927 9.20551 7.30879e-08L4.79001 0.00950007C4.67176 0.0102501 4.55701 0.0575001 4.47376 0.14125C4.39001 0.22475 4.34326 0.34025 4.34326 0.45825L4.34476 1.221L5.24051 1.219L5.23951 0.90375L8.75951 0.89625L8.76051 1.2115Z"
        fill="#4D495A"
      />
      <path
        d="M0.0035 3.62825L14 3.598L13.9965 2.1725L12.645 2.17525L12.643 1.42525C12.643 1.36675 12.619 1.3095 12.5778 1.2685C12.5365 1.227 12.479 1.20375 12.4205 1.20375L9.6555 1.20975L8.76025 1.2115L5.24025 1.21925L4.3445 1.22125L1.57625 1.22725C1.5175 1.22725 1.4605 1.25125 1.4195 1.293C1.37825 1.33425 1.3545 1.3915 1.3545 1.44975L1.3565 2.20025L0 2.20325L0.0035 3.62875V3.62825Z"
        fill="#4D495A"
      />
      <path
        d="M0.751709 4.0385V16.0235H13.2485V4.0385H0.751709ZM4.15546 14.8967C4.15546 14.9532 4.13396 15.0065 4.09396 15.0468C4.05346 15.0868 4.00046 15.1087 3.94371 15.1087H2.71121C2.65496 15.1087 2.60096 15.0862 2.56121 15.0468C2.52171 15.0072 2.49871 14.9525 2.49871 14.8967V5.5105C2.49871 5.4545 2.52146 5.39975 2.56121 5.36025C2.60046 5.32075 2.65521 5.298 2.71121 5.298H3.94371C3.99971 5.298 4.05446 5.32075 4.09396 5.36025C4.13346 5.39975 4.15546 5.4545 4.15546 5.5105V14.8967ZM7.82846 14.8967C7.82846 14.9037 7.82821 14.9108 7.82746 14.9178C7.82271 14.9665 7.80121 15.0115 7.76621 15.0468C7.72571 15.0868 7.67246 15.1087 7.61596 15.1087H6.38346C6.32771 15.1087 6.27346 15.0862 6.23321 15.0468C6.19371 15.0072 6.17096 14.9525 6.17096 14.8967V5.5105C6.17096 5.5035 6.17121 5.4965 6.17196 5.4895C6.17671 5.441 6.19846 5.39475 6.23296 5.36025C6.27246 5.32075 6.32721 5.298 6.38296 5.298H7.61546C7.62246 5.298 7.62921 5.29825 7.63621 5.299C7.68321 5.30375 7.72821 5.3245 7.76246 5.35725C7.76346 5.35825 7.76446 5.359 7.76546 5.36C7.80496 5.3995 7.82771 5.45425 7.82771 5.51025V14.8965L7.82846 14.8967ZM11.5012 14.8967C11.5012 14.9532 11.479 15.0065 11.4385 15.0468C11.3985 15.0868 11.3455 15.1087 11.2887 15.1087H10.0562C10.0005 15.1087 9.94571 15.0862 9.90596 15.0468C9.86646 15.0072 9.84371 14.9525 9.84371 14.8967V5.5105C9.84371 5.4545 9.86646 5.39975 9.90596 5.36025C9.94546 5.321 9.99996 5.298 10.056 5.298H11.2885C11.3447 5.298 11.3992 5.32075 11.4382 5.36025C11.478 5.39975 11.501 5.4545 11.501 5.5105V14.8967H11.5012Z"
        fill="#4D495A"
      />
    </g>
    <defs>
      <clipPath id="clip0_24_2412">
        <rect width="14" height="16.0238" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const RightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="17"
    viewBox="0 0 10 17"
    fill="none"
  >
    <path d="M1 0.5L9 8.5L1 16.5" stroke="#5C576B" />
  </svg>
);
